<template>
  <div class="bdp" :class="[`bdp-${size}`, { 'bdp-open': open, 'bdp-disabled': disabled, 'bdp-invalid': invalid, 'bdp-block': block, 'bdp-has-clear': showInlineClear }, $attrs.class]"
    :style="$attrs.style">
    <button
      :id="id"
      ref="trigger"
      type="button"
      class="bdp-trigger"
      :disabled="disabled"
      :aria-label="ariaLabel || undefined"
      :aria-describedby="describedBy"
      :aria-invalid="invalid ? 'true' : undefined"
      aria-haspopup="dialog"
      :aria-expanded="open ? 'true' : 'false'"
      @click.stop="toggle"
      @keydown="onTriggerKeydown"
    >
      <span :id="valueId" class="bdp-value" :class="{ 'bdp-placeholder': !selectedDate }">
        {{ selectedDate ? formatLabel(selectedDate) : placeholder }}
      </span>
      <Calendar class="bdp-icon" :size="16" aria-hidden="true" />
    </button>
    <button v-if="showInlineClear" type="button" class="bdp-inline-clear" :disabled="disabled"
      :aria-label="`Clear ${ariaLabel || 'date'}`" @click.stop="clear">
      <X :size="14" aria-hidden="true" />
    </button>

    <!-- On <body>, like BaseSelect's menu, so no modal or drawer can clip it. -->
    <teleport to="body">
      <transition name="bdp-pop">
        <div v-if="open" ref="pop" class="bdp-pop" :class="{ 'bdp-pop-up': placeUp }" :style="popStyle"
          role="dialog" aria-modal="true" :aria-label="`Choose ${ariaLabel || 'a date'}`"
          @mousedown.stop @keydown="onPopKeydown">
          <div class="bdp-head">
            <button type="button" class="bdp-nav" aria-label="Previous month" :disabled="!canGoPrev"
              @click="shiftMonth(-1)">
              <ChevronLeft :size="18" aria-hidden="true" />
            </button>
            <span :id="monthId" class="bdp-month" aria-live="polite">{{ monthLabel }}</span>
            <button type="button" class="bdp-nav" aria-label="Next month" :disabled="!canGoNext"
              @click="shiftMonth(1)">
              <ChevronRight :size="18" aria-hidden="true" />
            </button>
          </div>

          <div class="bdp-grid" role="grid" :aria-labelledby="monthId" @keydown="onGridKeydown">
            <div class="bdp-row bdp-weekdays" role="row">
              <span v-for="d in weekdays" :key="d.short" class="bdp-weekday" role="columnheader"
                :aria-label="d.long">{{ d.short }}</span>
            </div>
            <div v-for="(week, w) in weeks" :key="w" class="bdp-row" role="row">
              <button v-for="day in week" :key="day.iso" type="button" role="gridcell"
                class="bdp-day"
                :class="{ 'bdp-muted': !day.inMonth, 'bdp-today': day.isToday, 'bdp-selected': day.isSelected }"
                :data-iso="day.iso"
                :tabindex="day.iso === focusedIso ? 0 : -1"
                :disabled="day.disabled"
                :aria-selected="day.isSelected ? 'true' : 'false'"
                :aria-current="day.isToday ? 'date' : undefined"
                :aria-label="formatLong(day.date)"
                @click="choose(day)">
                {{ day.label }}
              </button>
            </div>
          </div>

          <div class="bdp-foot">
            <button type="button" class="bdp-link" :disabled="!todayAllowed" @click="chooseToday">Today</button>
            <button v-if="clearable && modelValue" type="button" class="bdp-link" @click="clear">Clear</button>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
// One date picker for the whole app, in place of <input type="date">.
//
// v-model is the same 'YYYY-MM-DD' string a native date input uses ('' for
// none), so it drops in without touching the data around it. `min` / `max`
// take the same strings and disable the days outside them. Dates are handled
// as local calendar days throughout — never through toISOString(), which
// shifts a date across midnight for anyone east or west of UTC.
//
// It looks and opens like BaseSelect: a field, and a panel on <body> beside
// it. The panel is a small dialog: focus moves onto the chosen day, the arrow
// keys walk the calendar, Enter picks, Esc closes back to the field.

import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-vue-next';

let uid = 0;

const GAP = 6;
const POP_WIDTH = 300;
const POP_HEIGHT = 360;

const pad = (n) => String(n).padStart(2, '0');
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromIso = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
// Same day-of-month in another month, pulled back to that month's last day
// (31 Jan + 1 month = 28/29 Feb, not 2/3 Mar).
const addMonths = (d, n) => {
  const first = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  return new Date(first.getFullYear(), first.getMonth(), Math.min(d.getDate(), last));
};

const LABEL_FORMAT = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const LONG_FORMAT = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const MONTH_FORMAT = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' });
// Sunday first, as the task form's calendar has always been.
const WEEKDAYS = [
  { short: 'Su', long: 'Sunday' }, { short: 'Mo', long: 'Monday' }, { short: 'Tu', long: 'Tuesday' },
  { short: 'We', long: 'Wednesday' }, { short: 'Th', long: 'Thursday' }, { short: 'Fr', long: 'Friday' },
  { short: 'Sa', long: 'Saturday' },
];

export default {
  name: 'BaseDatePicker',
  components: { Calendar, ChevronLeft, ChevronRight, X },
  // class/style go on the wrapper; everything else (aria-describedby from a
  // field's error message, data-*) is read explicitly or left on the wrapper.
  inheritAttrs: false,
  props: {
    modelValue: { type: String, default: '' },
    min: { type: String, default: '' },
    max: { type: String, default: '' },
    placeholder: { type: String, default: 'Select date' },
    disabled: { type: Boolean, default: false },
    invalid: { type: Boolean, default: false },
    // Offer a way back to "no date" — for filters, not required fields.
    clearable: { type: Boolean, default: false },
    // md: forms. sm: filters and dense rows. Same heights as BaseSelect.
    size: { type: String, default: 'md' },
    block: { type: Boolean, default: false },
    ariaLabel: { type: String, default: '' },
    id: { type: String, default: undefined },
  },
  emits: ['update:modelValue', 'change'],
  data() {
    uid += 1;
    return {
      open: false,
      view: new Date(),        // any day in the month on show
      focusedIso: '',
      placeUp: false,
      popStyle: {},
      valueId: `bdp-value-${uid}`,
      monthId: `bdp-month-${uid}`,
    };
  },
  computed: {
    selectedDate() {
      return fromIso(this.modelValue);
    },
    minDate() {
      return fromIso(this.min);
    },
    maxDate() {
      return fromIso(this.max);
    },
    // The field is named by its <label>; this makes the chosen date heard too.
    describedBy() {
      return [this.valueId, this.$attrs['aria-describedby']].filter(Boolean).join(' ');
    },
    showInlineClear() {
      return this.clearable && Boolean(this.modelValue);
    },
    weekdays() {
      return WEEKDAYS;
    },
    monthLabel() {
      return MONTH_FORMAT.format(this.view);
    },
    weeks() {
      const first = new Date(this.view.getFullYear(), this.view.getMonth(), 1);
      const start = addDays(first, -first.getDay());
      const todayIso = toIso(new Date());
      const rows = [];
      for (let w = 0; w < 6; w += 1) {
        const row = [];
        for (let i = 0; i < 7; i += 1) {
          const date = addDays(start, w * 7 + i);
          const iso = toIso(date);
          row.push({
            date,
            iso,
            label: date.getDate(),
            inMonth: date.getMonth() === this.view.getMonth(),
            isToday: iso === todayIso,
            isSelected: iso === this.modelValue,
            disabled: !this.inRange(date),
          });
        }
        rows.push(row);
      }
      return rows;
    },
    canGoPrev() {
      if (!this.minDate) return true;
      return new Date(this.view.getFullYear(), this.view.getMonth(), 0) >= this.minDate;
    },
    canGoNext() {
      if (!this.maxDate) return true;
      return new Date(this.view.getFullYear(), this.view.getMonth() + 1, 1) <= this.maxDate;
    },
    todayAllowed() {
      return this.inRange(new Date());
    },
  },
  beforeUnmount() {
    this.unbind();
  },
  methods: {
    formatLabel(d) {
      return LABEL_FORMAT.format(d);
    },
    formatLong(d) {
      return LONG_FORMAT.format(d);
    },
    inRange(d) {
      const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      if (this.minDate && day < this.minDate) return false;
      if (this.maxDate && day > this.maxDate) return false;
      return true;
    },
    clamp(d) {
      if (this.minDate && d < this.minDate) return this.minDate;
      if (this.maxDate && d > this.maxDate) return this.maxDate;
      return d;
    },

    toggle() {
      if (this.open) this.close(true);
      else this.openPicker();
    },
    async openPicker() {
      if (this.disabled || this.open) return;
      const start = this.clamp(this.selectedDate || new Date());
      this.view = start;
      this.focusedIso = toIso(start);
      this.open = true;
      this.position();
      this.bind();
      await this.$nextTick();
      this.position();
      this.focusDay();
    },
    close(refocus = false) {
      if (!this.open) return;
      this.open = false;
      this.unbind();
      if (refocus) this.$refs.trigger?.focus();
    },
    emitValue(iso) {
      if (iso === this.modelValue) return;
      this.$emit('update:modelValue', iso);
      this.$emit('change', iso);
    },
    choose(day) {
      if (day.disabled) return;
      this.emitValue(day.iso);
      this.close(true);
    },
    chooseToday() {
      if (!this.todayAllowed) return;
      this.emitValue(toIso(new Date()));
      this.close(true);
    },
    // From the panel's Clear link or the field's inline ✕; focus lands back on
    // the field either way.
    clear() {
      const wasOpen = this.open;
      this.emitValue('');
      if (wasOpen) this.close(true);
      else this.$refs.trigger?.focus();
    },

    // Keyboard ---------------------------------------------------------------
    onTriggerKeydown(e) {
      if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault();
        this.openPicker();
      }
    },
    onPopKeydown(e) {
      if (e.key === 'Escape') {
        // Handled here, so a dialog around the field (v-dialog) stays open.
        e.preventDefault();
        this.close(true);
        return;
      }
      if (e.key === 'Tab') this.trapTab(e);
    },
    onGridKeydown(e) {
      const current = fromIso(this.focusedIso) || new Date();
      const moves = {
        ArrowLeft: () => addDays(current, -1),
        ArrowRight: () => addDays(current, 1),
        ArrowUp: () => addDays(current, -7),
        ArrowDown: () => addDays(current, 7),
        Home: () => addDays(current, -current.getDay()),
        End: () => addDays(current, 6 - current.getDay()),
        PageUp: () => addMonths(current, e.shiftKey ? -12 : -1),
        PageDown: () => addMonths(current, e.shiftKey ? 12 : 1),
      };
      if (moves[e.key]) {
        e.preventDefault();
        this.moveFocus(this.clamp(moves[e.key]()));
      }
      // Enter / Space fall through to the focused day button's own click.
    },
    moveFocus(date) {
      this.focusedIso = toIso(date);
      if (date.getMonth() !== this.view.getMonth() || date.getFullYear() !== this.view.getFullYear()) {
        this.view = date;
      }
      this.$nextTick(this.focusDay);
    },
    shiftMonth(n) {
      const target = this.clamp(addMonths(fromIso(this.focusedIso) || this.view, n));
      this.focusedIso = toIso(target);
      this.view = target;
    },
    focusDay() {
      const pop = this.$refs.pop;
      if (!pop) return;
      const el = pop.querySelector(`[data-iso="${this.focusedIso}"]:not([disabled])`)
        || pop.querySelector('.bdp-day:not([disabled])');
      el?.focus();
    },
    trapTab(e) {
      const items = Array.from(this.$refs.pop.querySelectorAll('button:not([disabled])'))
        .filter(b => !b.classList.contains('bdp-day') || b.tabIndex === 0);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },

    // Placement --------------------------------------------------------------
    position() {
      const t = this.$refs.trigger;
      if (!t) return;
      const r = t.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - GAP;
      const above = r.top - GAP;
      this.placeUp = below < POP_HEIGHT && above > below;
      const width = Math.min(POP_WIDTH, window.innerWidth - 16);
      const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
      const style = { left: `${left}px`, width: `${width}px` };
      if (this.placeUp) style.bottom = `${window.innerHeight - r.top + GAP}px`;
      else style.top = `${r.bottom + GAP}px`;
      this.popStyle = style;
    },
    bind() {
      this._outside = (e) => {
        if (this.$el.contains(e.target) || this.$refs.pop?.contains(e.target)) return;
        this.close();
      };
      this._reposition = () => this.position();
      document.addEventListener('mousedown', this._outside, true);
      window.addEventListener('scroll', this._reposition, true);
      window.addEventListener('resize', this._reposition);
    },
    unbind() {
      if (this._outside) document.removeEventListener('mousedown', this._outside, true);
      if (this._reposition) {
        window.removeEventListener('scroll', this._reposition, true);
        window.removeEventListener('resize', this._reposition);
      }
      this._outside = null;
      this._reposition = null;
    },
  },
};
</script>

<style scoped>
.bdp {
  position: relative;
  display: inline-flex;
  min-width: 0;
  font-family: inherit;
}

.bdp-block {
  display: flex;
  width: 100%;
}

/* Same field as BaseSelect's trigger, so a form reads as one set of controls. */
.bdp-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  width: 100%;
  min-width: 0;
  height: 42px;
  padding: 0 0.9rem 0 1rem;
  background: var(--color-surface);
  border: 1.5px solid var(--color-border);
  border-radius: 12px;
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-base);
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.bdp-trigger:hover:not(:disabled) {
  border-color: var(--primary-400, var(--primary-400));
}

.bdp-trigger:focus-visible,
.bdp-open .bdp-trigger {
  outline: none;
  border-color: var(--primary-500, var(--primary-500));
  box-shadow: 0 0 0 4px rgba(0, 132, 255, 0.12);
}

.bdp-invalid .bdp-trigger {
  border-color: var(--error-500, var(--error-500));
}

.bdp-invalid .bdp-trigger:focus-visible,
.bdp-invalid.bdp-open .bdp-trigger {
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.12);
}

.bdp-disabled .bdp-trigger {
  cursor: not-allowed;
  opacity: 0.6;
  background: var(--color-surface-subtle);
}

.bdp-value {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bdp-placeholder {
  color: var(--color-text-muted);
  font-weight: 400;
}

.bdp-icon {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

/* Room for the inline ✕, which sits over the right end of the field. */
.bdp-has-clear .bdp-trigger {
  padding-right: 2.4rem;
}

.bdp-has-clear .bdp-icon {
  display: none;
}

.bdp-inline-clear {
  position: absolute;
  top: 50%;
  right: 0.45rem;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
}

.bdp-inline-clear:hover {
  background: var(--color-surface-muted);
  color: var(--color-text-secondary);
}

.bdp-sm .bdp-trigger {
  height: 34px;
  padding: 0 0.7rem 0 0.8rem;
  border-radius: 9px;
  font-size: var(--text-md);
}

.bdp-sm.bdp-has-clear .bdp-trigger {
  padding-right: 2.1rem;
}
</style>

<!-- Unscoped: the panel is teleported to <body>. Every selector is bdp- prefixed. -->
<style>
.bdp-pop {
  position: fixed;
  z-index: 10050;
  padding: 0.85rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.16);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.bdp-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.6rem;
}

.bdp-month {
  font-size: var(--text-lg);
  font-weight: 700;
  color: var(--color-text);
}

.bdp-nav {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: var(--color-text-secondary);
  cursor: pointer;
}

.bdp-nav:hover:not(:disabled) {
  background: var(--color-surface-muted);
}

.bdp-nav:disabled {
  color: var(--gray-300);
  cursor: default;
}

.bdp-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.bdp-weekday {
  padding: 0.3rem 0;
  font-size: var(--text-xs);
  font-weight: 700;
  text-align: center;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.bdp-day {
  height: 36px;
  border: none;
  border-radius: 10px;
  background: transparent;
  font: inherit;
  font-size: var(--text-base);
  font-weight: 500;
  color: var(--color-text);
  cursor: pointer;
}

.bdp-day:hover:not(:disabled):not(.bdp-selected) {
  background: var(--primary-50, var(--primary-50));
  color: var(--primary-700, var(--primary-700));
}

/* Days of the neighbouring months: shown for the grid's shape, still usable. */
.bdp-day.bdp-muted {
  color: var(--color-text-muted);
}

/* Today: a ring, so it never reads like the selected day. */
.bdp-day.bdp-today {
  box-shadow: inset 0 0 0 1.5px var(--primary-500, var(--primary-500));
  font-weight: 700;
}

.bdp-day.bdp-selected {
  background: var(--fixed-primary-600);
  color: #fff;
  font-weight: 700;
}

.bdp-day:disabled {
  color: var(--gray-300);
  text-decoration: line-through;
  cursor: not-allowed;
}

.bdp-day:focus-visible,
.bdp-nav:focus-visible,
.bdp-link:focus-visible {
  outline: 2px solid var(--primary-600, var(--primary-600));
  outline-offset: 1px;
}

.bdp-foot {
  display: flex;
  justify-content: space-between;
  margin-top: 0.6rem;
  padding-top: 0.6rem;
  border-top: 1px solid var(--gray-100);
}

.bdp-link {
  padding: 0.35rem 0.6rem;
  border: none;
  border-radius: 8px;
  background: transparent;
  font: inherit;
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--primary-600, var(--primary-600));
  cursor: pointer;
}

.bdp-link:hover:not(:disabled) {
  background: var(--primary-50, var(--primary-50));
}

.bdp-link:disabled {
  color: var(--color-icon-muted);
  cursor: default;
}

.bdp-pop-enter-active,
.bdp-pop-leave-active {
  transition: opacity 0.12s ease;
}

.bdp-pop-enter-from,
.bdp-pop-leave-to {
  opacity: 0;
}
</style>
